import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from 'react-apollo'
import Input from '../forms/input'
import Button from '../button'
import styles from './password-reset-form.css'
import { getGQLErrorMsg } from '../../lib/apollo'
import { FORM_MESSAGES } from '../../constants'
import { REGEX } from '../../utils/regex'
import { useMutateAndLoading } from '../../hooks/use-mutate-and-loading'
import { E2E_SELECTORS } from '../../e2e-selectors'
import RESET_PASSWORD_REQUEST_MUTATION from '../../../graphql/mutations/request-password-reset.gql'

/**
 * used by RequestPasswordReset.
 * Handles form validation and request mutation requestPasswordReset.
 */
export default function RequestPasswordResetForm({ email, onRequestSuccess }) {
  const [submitError, setSubmitError] = useState(null)
  const history = useHistory()
  const location = useLocation()
  const [requestPasswordResetMutation] = useMutation(
    RESET_PASSWORD_REQUEST_MUTATION
  )
  const mutateAndLoading = useMutateAndLoading(requestPasswordResetMutation)

  const onCancel = useCallback(() => {
    history.push(location.pathname)
  }, [history, location.pathname])

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      email: email || ''
    },
    mode: 'onChange'
  })

  const onSubmit = async (values) => {
    setSubmitError(null)

    try {
      const data = await mutateAndLoading('requestPasswordReset', {
        variables: {
          email: values.email
        }
      })

      if (data?.requestPasswordReset) {
        onRequestSuccess(values.email)
        reset({ email: values.email })
      }
    } catch (error) {
      const errorPayload = {
        errorMessage: getGQLErrorMsg(error),
        email: values.email
      }
      setSubmitError(errorPayload)
      setError('email', { message: errorPayload.errorMessage })
    }
  }

  const title = 'Reset password - NEON'
  const isSubmittingClass = isSubmitting ? styles.isSubmitting : ''
  const currentEmailValue = watch('email')
  const hasSubmitError =
    submitError &&
    currentEmailValue === submitError.email &&
    submitError.errorMessage

  return (
    <form
      className={`${styles.form} ${isSubmittingClass}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className={styles.formRow}>
        <div className={styles.formRowItem}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: FORM_MESSAGES.required,
              pattern: {
                value: REGEX.email,
                message: FORM_MESSAGES.email.valid
              }
            }}
            render={({ field }) => (
              <Input
                label="Email"
                name={field.name}
                labelClass={styles.label}
                inputClass={styles.input}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                warning={
                  (field.value && errors.email?.message) ||
                  (submitError &&
                    field.value === submitError.email &&
                    submitError.errorMessage)
                }
                isValid={!submitError && Boolean(field.value && !errors.email)}
              />
            )}
          />
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <div className={styles.saveWrapper}>
          <Button
            disabled={
              isSubmitting || Boolean(hasSubmitError)
            }
            e2eIdentifier={E2E_SELECTORS.RESET_SUBMIT_BUTTON}
            text="Reset"
          />
        </div>
        <div className={styles.cancelWrapper}>
          <Button
            disabled={isSubmitting}
            text="Cancel"
            onClick={() => {
              onCancel()
            }}
            darkBGSecondary
          />
        </div>
      </div>
    </form>
  )
}

RequestPasswordResetForm.propTypes = {
  email: PropTypes.string,
  onRequestSuccess: PropTypes.func.isRequired
}

RequestPasswordResetForm.defaultProps = {
  email: ''
}
